import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

// Types
import { ReviewState } from 'Shared/types/Review.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';

// Selectors
import * as ReviewsSelectors from 'Shared/selectors/Reviews.selectors';
import * as UserSelectors from 'Shared/selectors/Users.selectors';

// Actions
import * as ReviewFormsAction from 'Shared/actions/ReviewForms.actions';

// Helpers
import * as ReviewsHelpers from 'Shared/model_helpers/Reviews.helper';
import * as ReviewFormHelpers from 'Shared/model_helpers/ReviewForm.helper';
import * as UserHelpers from 'Shared/model_helpers/User.helpers';

// Components
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton, { Size } from 'Wpds/components/button/WpButton';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';

type Props = {
  review;
  showAsDropdown?: boolean;
  buttonSize?: Size;
};

const EditAndUnshareOptionsMenu = ({ review, showAsDropdown = true, buttonSize = 'sm' }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [isEditConfirmOpen, toggleIsEditConfirmOpen] = useToggle(false);

  const reviewSlug = ReviewsHelpers.getSlug(review);
  const compilerSlug = ReviewsHelpers.getCompilerSlug(review);
  const state = ReviewsHelpers.getState(review);
  const receiverSlug = ReviewsHelpers.getReceiverSlug(review);

  const currentUserSlug = useSelector(state => state.user.slug);
  const reviewForm = useSelector(state =>
    ReviewsSelectors.getReviewFormsByReviewSlugAndType(state, { reviewSlug, formType: 'compilation' }).first()
  );

  const receiver = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: receiverSlug }));
  const receiverName = UserHelpers.getDisplayName(receiver);

  const reviewFormSlug = ReviewFormHelpers.getSlug(reviewForm);

  const isCurrentUserCompiler = currentUserSlug === compilerSlug;
  const isShared = state === ReviewState.SHARED;
  const isCompletedAndNotShared = state === ReviewState.COMPILATION_COMPLETED_BUT_NOT_SHARED;

  const handleUnshareReview = () => {
    dispatch(ReviewFormsAction.unsubmitReviewForm(reviewFormSlug)).then(() => {
      history.push(PageUrlsUtils.makeReviewCompilationUrl(reviewSlug));
    });
  };

  const handleEditClick = () => {
    if (isShared) toggleIsEditConfirmOpen();
    else handleUnshareReview();
  };

  if (!isCurrentUserCompiler) return null;
  if (!(isShared || isCompletedAndNotShared)) return null;

  return (
    <>
      {showAsDropdown ? (
        <WpDropdown placement="bottom-end">
          <WpDropdownToggle>
            <WpButton variant="tertiary" size={buttonSize}>
              <WpIcon name="ellipsis-h-alt" />
            </WpButton>
          </WpDropdownToggle>
          <WpDropdownMenu>
            <WpDropdownMenuItem onClick={handleEditClick}>
              <WpIcon name="pencil" />
              {isShared ? 'Unshare & Edit' : 'Edit'}
            </WpDropdownMenuItem>
          </WpDropdownMenu>
        </WpDropdown>
      ) : (
        <WpButton onClick={handleEditClick} variant="tertiary" size={buttonSize}>
          <WpIcon name="pencil" />
          Edit
        </WpButton>
      )}
      {isEditConfirmOpen && (
        <ConfirmModal
          isOpen
          onToggle={toggleIsEditConfirmOpen}
          onConfirmClick={handleUnshareReview}
          confirmButtonLabel="Edit Review"
          title="Edit Review"
          confirmButtonColor="primary"
        >
          Just a heads up, we&apos;ll unshare this review, and when ready you will need to reshare it {receiverName}.
        </ConfirmModal>
      )}
    </>
  );
};

export default EditAndUnshareOptionsMenu;
